import {
	RateCardType,
	TRateCardModifierSet,
	TRateCardModifierType,
	TRateCardPerStopModifier,
} from '@types';
import {
	ContentExpander,
	Grid,
	NumberField,
	Label,
	Radio,
	Spacer
} from '@components';
import styles from './Modifiers.module.scss';
import { useRecoilValue } from 'recoil';
import {
	RateCardFormErrors, RateCardSettingsTypes, RateCardTypes, AllRateCardFuelLevyModifiers, AllRateCardSettingFuelLevyModifiers
} from '@state';
import { useClearRCSettingsError, useRateCardModifiersState } from '@hooks';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { getRateCardIndex, removeLeadingZeros } from '@utils';
import { ModifierBadge } from './ModifierBadge';

type TProps = {
	isSettings?: boolean;
	type: RateCardType
	isDisabled?: boolean;
	itemTypeId?: string;
}

export const TimeSlotModifierPanel = (props: TProps) => {
	const {
		isSettings,
		type,
		isDisabled = false,
		itemTypeId
	} = props;
	const [rateCardModifiers, setRateCardModifiers] = useRateCardModifiersState();
	const rateCardSettingsTypes = useRecoilValue(RateCardSettingsTypes);
	const rateCardTypes = useRecoilValue(RateCardTypes);
	const rateCardTypeIndex = getRateCardIndex({
		rateCardSettingsTypes,
		rateCardTypes,
		itemTypeId,
		isSettings: !!isSettings,
		type
	});
	const modifiers = isSettings ? rateCardModifiers.settings : rateCardModifiers.rateCard;
	const errorString = isSettings ? 'rate_card_types' : 'rate_card_prices';
	const rcFormErrors = useRecoilValue(RateCardFormErrors);
	const clearRCSettingsError = useClearRCSettingsError();

	const allRateCardModifiers = useRecoilValue(AllRateCardFuelLevyModifiers);
	const allRateCardSettingModifiers = useRecoilValue(AllRateCardSettingFuelLevyModifiers);
	const fuelLevyModifiers = isSettings ? allRateCardSettingModifiers : allRateCardModifiers;

	const updateTimeSlotModifier = (
		key: keyof TRateCardPerStopModifier,
		newValue: string,
	) => {
		const update = {
			...modifiers,
			time_slot: newValue === 'None' ? null : {
				...modifiers?.time_slot,
				...(key === 'type' ? { value: '' } : {}),
				[key]: String(newValue),
				attracts_fuel_levy: !!fuelLevyModifiers?.time_slot?.attracts_fuel_levy
			}
		} as TRateCardModifierSet;

		const newModifiers = {
			settings: isSettings ? update : undefined,
			rateCard: !isSettings ? update : undefined,
		}

		setRateCardModifiers(newModifiers);
		// @ts-ignore
		if (rcFormErrors?.[errorString]?.[rateCardTypeIndex].modifier_set.time_slot[key]) {
			clearRCSettingsError(`${errorString}.${rateCardTypeIndex}.modifier_set.time_slot.${key}`)
		}
	}

	const handleOnBlur = (
		modifierKey: keyof TRateCardPerStopModifier,
		newValue: string,
	) => {
		if (newValue.startsWith('0') && !newValue.startsWith('0.')) {
			const value = removeLeadingZeros(newValue);
			updateTimeSlotModifier(modifierKey, value);
		}
		if (newValue.startsWith('.')) {
			updateTimeSlotModifier(modifierKey, `0${newValue}`)
		}
	}

	const hasError = (key: string) => {
		// @ts-ignore
		return rcFormErrors?.[errorString]?.[rateCardTypeIndex]?.modifier_set?.time_slot?.[key]?._errors?.[0] || '';
	}

	const getSectionErrors = () => {
		const fieldsWithPossibleError = ['value']
		return fieldsWithPossibleError.map((field) =>
			hasError(field)
		).some((field) => field.length > 0);
	}

	const getConfiguredCount = () => {
		if (!modifiers?.time_slot) return 0;
		return parseFloat(`${modifiers.time_slot.value ?? ''}`) > 0 ? 1 : 0;
	}

	if (!modifiers) return

	return (
		<ContentExpander
			title={<div className={styles.expanderTitle}>Time slot
				<ModifierBadge count={getConfiguredCount()} />
				{
					getSectionErrors() ?
						<span className={styles.expanderErrorContainer}>
							<ErrorOutlineOutlinedIcon />
						</span>	: null
				}
			</div>}
			showSpacer
			expanded={false}
			arrowsAsExpanderIcon
			iconColor='var(--neutral-06)'
		>
			<div className={styles.expanderContent}>
				<Label>Charge method</Label>
				<Radio.Group className={styles.radioContainer}>
					<Radio
						name={'charge_method'}
						label={'Fee per stop'}
						value={TRateCardModifierType.PER_STOP}
						onChange={(e) => updateTimeSlotModifier('type', e.target.value)}
						checked={modifiers?.time_slot?.type === TRateCardModifierType.PER_STOP}
						disabled={isDisabled}
					/>
					<Radio
						name={'charge_method'}
						label={'None'}
						value={'None'}
						onChange={(e) => updateTimeSlotModifier('type', e.target.value)}
						checked={modifiers?.time_slot === null}
						disabled={isDisabled}
					/>
				</Radio.Group>
				{
					modifiers?.time_slot?.type &&
						<Grid columns={3} columnTemplate={'repeat(3, 1fr)'} gap={'16px'} alignItems='start'>
							{
								modifiers?.time_slot?.type === TRateCardModifierType.PER_STOP &&
									<>
										<NumberField
											name={'value'}
											value={`${modifiers.time_slot?.value ?? ''}`}
											onChange={(e) => updateTimeSlotModifier('value', e.target.value)}
											leftModule={<span className={styles.prefix}>$</span>}
											label={'Fee per stop'}
											keepErrorSpace={false}
											required
											hasError={!!hasError('value')}
											errorMessage={hasError('value')}
											disabled={isDisabled}
											min={'0'}
											onBlur={(e) => handleOnBlur('value', e.target.value)}
										/>
									</>
							}
						</Grid>
				}
				<Spacer size={5} />
			</div>

		</ContentExpander>
	);
}